import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface NavigationSearchProps {
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  isOpen: boolean;
  onToggle: () => void;
  className?: string;
}

export const NavigationSearch: React.FC<NavigationSearchProps> = ({
  value,
  placeholder = 'Search...',
  onChange,
  onClear,
  isOpen,
  onToggle,
  className
}) => {
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (isOpen) {
      const id = setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [isOpen]);

  if (!isOpen) {
    return (
      <Button
        variant="outline"
        size="sm"
        className={cn(
          "rounded-full px-3",
          "md:bg-sky-100/90 md:text-sky-900 md:border-sky-200/50 md:hover:bg-sky-100",
          "lg:h-8 lg:bg-white lg:text-slate-800 lg:border-gray-200 lg:ring-1 lg:ring-black/10 lg:shadow-sm lg:hover:bg-gray-50",
          className
        )}
        type="button"
        aria-label="Search"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls="nav-search-input"
      >
        <Search className="size-4" />
        <span className="hidden lg:inline-block ml-2 text-xs text-muted-foreground">⌘K</span>
      </Button>
    );
  }

  return (
    <div className={cn("flex-1 max-w-4xl px-2 w-full", className)}>
      <div className="relative">
        <Search 
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-500" 
          aria-hidden="true" 
        />
        <Input
          ref={searchInputRef}
          id="nav-search-input"
          value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          placeholder={placeholder}
          className="h-9 w-full rounded-full bg-white pl-10 pr-10 text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
          aria-label="Search"
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              onClear?.();
              onToggle();
            }
          }}
        />
        <button
          type="button"
          onClick={() => { onClear?.(); onToggle(); }}
          aria-label="Close search"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-500 hover:bg-gray-100"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
};

NavigationSearch.displayName = 'NavigationSearch';
